import { memo, Suspense } from "react";
import Image from "@Components/Image";
import {
  getPricingCurrency,
  getFeaturedImage,
} from "@Pages/PropertyPage/components/RoomTypeSection/utils";
import getFormattedTags from "@Pages/PropertyPage/components/RoomTypeSection/utils/getFormattedTags";
import getImagePath from "@Utils/getImagePath";
import styles from "./RoomTypeCard.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { useHistory } from "hooks/useHistory";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import {
  getVideoModalData,
  getGalleryModalData,
} from "@Pages/PropertyPage/data/propertyPageReducer";
import Button from "@Components/Button";
import useWhitelabel from "hooks/useWhitelabel";

interface RoomTypeCardProps {
  roomType: any;
  canonicalName?: string;
  /**
   * Optional override for the "View Room" click. When provided, it fully
   * replaces the default navigation (used e.g. by the review media gallery,
   * which needs to close its own modal/panel before scrolling to the room).
   * Receives the resolved room id (may be undefined).
   */
  onViewRoom?: (roomId?: string) => void;
}

const getPrice = (pricing: any): string => {
  if (!pricing) return "";

  const currency = getPricingCurrency(pricing.currency) || "£";
  const price = pricing.min_price || pricing.price;
  if (!price) return "";

  const duration = pricing.duration || "week";
  return `${currency}${price.toLocaleString("en")}/${duration}`;
};

// Removed local getAmenityTags in favor of centralized getFormattedTags utility

const RoomTypeCard = ({ roomType, canonicalName, onViewRoom }: RoomTypeCardProps) => {
  const { t } = useTranslationHook();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isWhitelabel } = useWhitelabel();

  const isInModal = useSelector(
    ({ propertyPageData }: AppState) => propertyPageData?.video_modal?.show || false,
  );
  // Property-level images used as a fallback when the room type has none — same
  // as the RoomTypeSection card logic.
  const propertyImages = useSelector(
    ({ propertyPageData }: AppState) => propertyPageData?.data?.images || [],
  );

  if (!roomType) return null;

  // Match the RoomTypeSection featured-image logic: prefer the room type's own
  // images, fall back to the property's images, then pick the featured one.
  const featuredImage =
    roomType?.images?.length > 0
      ? getFeaturedImage(roomType.images)
      : getFeaturedImage(propertyImages);
  const thumbnailUrl = featuredImage?.path ? getImagePath(featuredImage.path, "small") : "";
  const name = roomType?.name || "";
  const price = getPrice(roomType?.pricing);
  const tags = getFormattedTags(roomType, t);

  // Poll for the target room card and scroll to it. The room section renders
  // asynchronously after navigation, so a single fixed timeout can fire before
  // React commits the cards to the DOM.
  const scrollToRoom = (roomId?: string) => {
    let attempts = 0;
    const maxAttempts = 40; // ~4s at 100ms intervals
    const intervalId = setInterval(() => {
      attempts += 1;

      const roomCard = roomId && document.getElementById(`room-${roomId}`);
      if (roomCard) {
        const headerOffset = 54 + 48; // padding plus navbar height
        const offsetPosition =
          roomCard.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        clearInterval(intervalId);
        return;
      }

      // The specific room card may be hidden behind "View Sold Out Rooms" —
      // reveal all still-collapsed sold-out groups (same as the Floor Plan
      // section's "View Room") so the target card can render.
      const soldOutToggles = document.querySelectorAll<HTMLElement>(
        '[data-testid="sold-out-rooms-toggle"][data-collapsed="true"]',
      );
      soldOutToggles.forEach((toggle) => toggle.click());

      if (attempts >= maxAttempts) {
        // Fallback: scroll to the room section if the specific card never appears.
        const section = document.getElementById("room_type");
        if (section) {
          const headerOffset = 54 + 48;
          const offsetPosition =
            section.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
        clearInterval(intervalId);
      }
    }, 100);
  };

  const handleViewRoomClick = () => {
    if (onViewRoom) {
      onViewRoom(roomType?.id);
      return;
    }
    if (canonicalName) {
      const roomId = roomType?.id;
      const hash = roomId ? `#room-${roomId}` : "#room_type";
      if (isInModal) {
        dispatch(getVideoModalData({ show: false, videoId: "" }));
        dispatch(getGalleryModalData({ show: false, selectedTab: "all", data: {} }));
      }
      history.push(`/places/${canonicalName}${hash}`);
      scrollToRoom(roomId);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.topRow}>
        {thumbnailUrl && (
          <Image
            className={styles.thumbnail}
            src={thumbnailUrl}
            alt={name}
            width={128}
            height={128}
          />
        )}
        <div className={styles.content}>
          <h3 className={styles.roomName}>{name}</h3>
          {tags.length > 0 && (
            <div className={styles.amenities}>
              {tags.map((tag: any, index: number) => (
                <span key={`${tag.text}-${index}`} className={styles.amenityTag}>
                  {__CLIENT__ ? (
                    <Suspense fallback={<div style={{ width: 18, height: 18 }} />}>
                      {tag.icon}
                    </Suspense>
                  ) : (
                    <div style={{ width: 18, height: 18 }} />
                  )}
                  <span>{tag.text}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.priceWrap}>
          {price && (
            <>
              {/* Price prefix label */}
              <span className={styles.priceLabel}>{t("propertypage.videosection.from")}</span>
              <span className={styles.price}>{price}</span>
            </>
          )}
        </div>
        {/* Button label */}
        <Button
          variant={isWhitelabel ? "whiteLabel_primary" : "primary"}
          className={styles.bookButton}
          onClick={handleViewRoomClick}
        >
          {t("propertypage.videosection.viewroom")}
        </Button>
      </div>
    </div>
  );
};

export default wrapperHOC(memo(RoomTypeCard), {
  componentName: "RoomTypeCard",
  showForChina: true,
});
